import type { LocationDatapoint } from "@strtmn/j1939"
import { I18n } from "aws-amplify/utils"
import { icon } from "leaflet"
import React, { type PropsWithChildren } from "react"
import { FaLocationDot } from "react-icons/fa6"
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet"

import "leaflet/dist/leaflet.css"
import "./Widgets.css"

export interface TitleProps {
  title: string
}
export interface WidgetWithTitleProps extends TitleProps {
  IconNode?: React.ReactNode
  /**
   * Controls both the status dot color and (for PercentageWidget) the fill bar
   * color. Omit to render no dot.
   */
  indicatorTheme?: "ok" | "warn" | "error" | "unknown"
}

interface WidgetWrapperProps extends PropsWithChildren, WidgetWithTitleProps {
  className: string
}

function WidgetWrapper({
  className,
  title,
  IconNode,
  children,
  indicatorTheme,
}: WidgetWrapperProps) {
  return (
    <div className={`metric ${className}`}>
      <div className="m-top">
        {IconNode && <div className="m-icon">{IconNode}</div>}
        <div className="m-label">{title}</div>
        {indicatorTheme && <span className={`m-dot ${indicatorTheme}`} />}
      </div>
      {children}
    </div>
  )
}

interface BooleanTextWidgetProps extends WidgetWithTitleProps {
  value: boolean
  trueText?: string
  falseText?: string
}
export function BooleanTextWidget({
  value,
  trueText = "Ja",
  falseText = "Nein",
  ...wrapperProps
}: BooleanTextWidgetProps) {
  return (
    <WidgetWrapper className="boolean-text-widget" {...wrapperProps}>
      <div className="m-value">
        <span className="m-text">{value ? trueText : falseText}</span>
      </div>
    </WidgetWrapper>
  )
}

interface NumberWidgetProps extends WidgetWithTitleProps {
  value: number | string
  /** Multiplier applied before display (e.g. 0.001 for g → kg) */
  factor?: number | undefined
  /** Decimal places after rounding */
  precision?: number | undefined
  /** Optional unit suffix (e.g. "kg", "%") */
  unit?: string
}
export function NumberWidget({
  value,
  factor,
  precision = 2,
  unit,
  ...wrapperProps
}: NumberWidgetProps) {
  if (typeof value === "string") {
    value = parseFloat(value)
  }
  if (factor !== undefined) {
    value = value * factor
  }
  const displayed = parseFloat(value.toFixed(precision))
  return (
    <WidgetWrapper className="number-widget" {...wrapperProps}>
      <div className="m-value">
        <span className="m-num">{displayed}</span>
        {unit && <span className="m-unit">{unit}</span>}
      </div>
    </WidgetWrapper>
  )
}

interface PercentageWidgetProps extends WidgetWithTitleProps {
  value: number | string
  /** Decimal places after rounding */
  precision?: number | undefined
}
export function PercentageWidget({
  value,
  precision = 0,
  indicatorTheme,
  ...wrapperProps
}: PercentageWidgetProps) {
  const num = typeof value === "string" ? parseFloat(value) : value
  const displayed = parseFloat(num.toFixed(precision))
  return (
    <WidgetWrapper
      className="percentage-widget"
      indicatorTheme={indicatorTheme}
      {...wrapperProps}
    >
      <div className="m-value">
        <span className="m-num">{displayed}</span>
        <span className="m-unit">%</span>
      </div>
      <div className={`fill-bar${indicatorTheme ? ` ${indicatorTheme}` : ""}`}>
        <i style={{ width: `${Math.min(100, Math.max(0, num))}%` }} />
      </div>
    </WidgetWrapper>
  )
}

interface TextWidgetProps extends WidgetWithTitleProps {
  value: string
  unit?: string
}
export function TextWidget({ value, unit, ...wrapperProps }: TextWidgetProps) {
  return (
    <WidgetWrapper className="text-widget" {...wrapperProps}>
      <div className="m-value">
        <span className={unit ? "m-num" : "m-text"}>{value}</span>
        {unit && <span className="m-unit">{unit}</span>}
      </div>
    </WidgetWrapper>
  )
}

// The marker icon fix is from https://github.com/PaulLeCam/react-leaflet/issues/808#issuecomment-1605338973
const BslMarkerIcon = icon({
  iconUrl: "/marker-icon.png",
  iconRetinaUrl: "/marker-icon-2x.png",
  shadowUrl: "/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
})
interface MapWidgetProps extends WidgetWithTitleProps {
  location: LocationDatapoint
}
export function MapWidget({
  location,
  IconNode = <FaLocationDot />,
  indicatorTheme,
  ...wrapperProps
}: MapWidgetProps) {
  const hasLocation =
    location &&
    location.Latitude &&
    location.Latitude !== 0 &&
    location.Longitude &&
    location.Longitude !== 0
  const resolvedIndicator = indicatorTheme ?? (hasLocation ? "ok" : "unknown")
  if (!hasLocation) {
    return (
      <WidgetWrapper
        className="map-widget"
        IconNode={IconNode}
        indicatorTheme={resolvedIndicator}
        {...wrapperProps}
      >
        <div className="m-value">
          <span className="m-text">{I18n.get("Unbekannt")}</span>
        </div>
      </WidgetWrapper>
    )
  }
  const position = {
    lat: location.Latitude,
    lng: location.Longitude,
  }
  return (
    <WidgetWrapper
      className="map-widget"
      IconNode={IconNode}
      indicatorTheme={resolvedIndicator}
      {...wrapperProps}
    >
      <div className="m-value">
        <MapContainer center={position} zoom={13} scrollWheelZoom={true}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker icon={BslMarkerIcon} position={position}>
            <Popup>Standort des Fahrzeugs</Popup>
          </Marker>
        </MapContainer>
      </div>
    </WidgetWrapper>
  )
}
